/**
 * ProgressRing — 环形进度指示器（基础组件）
 *
 * 视觉对齐 Figma `Frame 1597883814`：浅灰背景圆环 + 可配色前景弧线。
 * 前景弧线长度按 `percent` (0–100) 动态计算。
 *
 * 实现原理：
 *  - 圆周长 C = 2π × r，通过 `stroke-dasharray` = C 和
 *    `stroke-dashoffset` = C × (1 - percent/100) 控制弧线显示比例。
 *  - 起点在 12 点方向（SVG rotate -90°）。
 */

import { FC } from 'react';

const DEFAULT_SIZE = 16;
const STROKE_RATIO = 1.333 / 16; // strokeWidth 与 size 的比例
const RADIUS_RATIO = 5.333 / 16; // radius 与 size 的比例

export interface ProgressRingProps {
  /** 进度百分比 0–100 */
  percent: number;
  /** SVG 尺寸（宽高），默认 16 */
  size?: number;
  /** 前景弧线颜色，默认 #737373 */
  color?: string;
  /** 背景圆环颜色，默认 #E5E5E5 */
  trackColor?: string;
  /** 自定义类名 */
  className?: string;
}

const ProgressRing: FC<ProgressRingProps> = ({
  percent,
  size = DEFAULT_SIZE,
  color = '#737373',
  trackColor = '#E5E5E5',
  className,
}) => {
  const clamped = Math.max(0, Math.min(100, percent));
  const radius = size * RADIUS_RATIO;
  const strokeWidth = size * STROKE_RATIO;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      className={className ?? 'shrink-0'}
      aria-hidden
    >
      {/* 背景圆环 */}
      <circle cx={size / 2} cy={size / 2} r={radius} stroke={trackColor} strokeWidth={strokeWidth} />
      {/* 前景弧线：长度随 percent 变化 */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
};

export default ProgressRing;
